/**
 * Alpine component that wires the PWA install affordance.
 *
 * - Chromium / Edge / Android: captures `beforeinstallprompt` and exposes
 *   `install()` to trigger the native dialog.
 * - iOS Safari (iPhone, iPad on iOS 13+): the event is not fired by Apple, so
 *   we expose `isIos` + `iosHint` so the view can render the manual
 *   "Share → Add to Home Screen" instruction instead.
 * - Already-installed (display-mode standalone or navigator.standalone on iOS):
 *   we suppress the affordance entirely.
 */
export default function installPrompt() {
    return {
        deferredPrompt: null,
        canInstall: false,
        isIos: false,
        iosHint: false,

        init() {
            this.isIos = isIosDevice();
            const standalone = isStandalone();

            if (this.isIos && !standalone) {
                // iOS gets the manual hint instead of a programmatic prompt.
                this.iosHint = true;
            }

            window.addEventListener('beforeinstallprompt', (e) => {
                e.preventDefault();
                this.deferredPrompt = e;
                this.canInstall = true;
            });

            window.addEventListener('appinstalled', () => {
                this.canInstall = false;
                this.iosHint = false;
                this.deferredPrompt = null;
            });
        },

        async install() {
            if (! this.deferredPrompt) return;

            this.deferredPrompt.prompt();
            await this.deferredPrompt.userChoice;
            this.deferredPrompt = null;
            this.canInstall = false;
        },

        dismiss() {
            this.canInstall = false;
            this.iosHint = false;
        },
    };
}

function isIosDevice() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    // iPadOS 13+ identifies as Mac; detect via touch points as a proxy.
    const iPadOnMac = ua.includes('Macintosh') && navigator.maxTouchPoints > 1;
    return /iPad|iPhone|iPod/.test(ua) || iPadOnMac;
}

function isStandalone() {
    if (typeof window === 'undefined') return false;
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        return true;
    }
    // iOS Safari exposes `navigator.standalone`.
    return Boolean(window.navigator && window.navigator.standalone);
}
